/* eslint-disable react-refresh/only-export-components */
import React, { CSSProperties, FC, ReactNode } from "react";

import type {
  CustomShadows,
  CustomTypeText,
  IgBorderColorsType,
  IgCoreBackgroundColorsType,
  IgIconColorType,
  IgPrimitivesType,
} from "@intelligems/ig-types";
import {
  Color,
  createTheme,
  PaletteMode,
  SxProps,
  ThemeProvider,
} from "@mui/material";
import { Palette, Theme } from "@mui/material/styles";
import {
  CommonColors,
  PaletteAugmentColorOptions,
  PaletteColor,
  PaletteTonalOffset,
  TypeAction,
  TypeBackground,
  TypeDivider,
  TypeText,
} from "@mui/material/styles/createPalette";

export type Modify<T, R> = Omit<T, keyof R> & R;

export const IgFontFamily = {
  heading: ['"Inter"', "sans-serif"].join(","),
  body: ['"Inter"', "sans-serif"].join(","),
  code: ["JetBrains Mono", "monospace"].join(","),
};

export const StripeColor = "#4F46E5";
export const ShopifyColor = "#96bf48";

export const IgShadows: CustomShadows = {
  low: "0px 4px 12px 0px rgba(0, 0, 0, 0.06), 0px 2px 6px 0px rgba(0, 0, 0, 0.04)",
  high: "0px 10px 38px -10px rgba(13, 18, 22, 0.35), 0px 10px 20px -15px rgba(14, 18, 22, 0.20)",
};

export const IgGrayPrimitives = {
  50: "#FFFFFF",
  100: "#FDFDFD",
  200: "#FAFAFA",
  300: "#F7F7F7",
  400: "#F3F3F3",
  500: "#F1F1F1",
  600: "#EBEBEB",
  700: "#E3E3E3",
  800: "#D4D4D4",
  900: "#CCCCCC",
  A100: "#B5B5B5",
  A200: "#8A8A8A",
  A300: "#616161",
  A400: "#4A4A4A",
  A500: "#303030",
  A600: "#1A1A1A",
};

export const IgBasePrimitives = {
  50: "#EEF4FF",
  100: "#D9E6FF",
  200: "#BCD3FF",
  300: "#8EB8FF",
  400: "#5991FF",
  500: "#2962FF", // Intelligems Brand Blue
  600: "#1B46F5",
  700: "#1433E1",
  800: "#172AB6",
  900: "#192A8F",
  A100: "#141B57",
  A200: "#3E6AC7",
};

export const IgBluePrimitives = {
  50: "#F4F7FD",
  100: "#E9F0FB",
  200: "#DDE7F8",
  300: "#CFDDF4",
  400: "#C0D1F0",
  500: "#B1C5EB",
  600: "#A0B6E4",
  700: "#8FA6DC",
  800: "#7D95D4",
  900: "#6C85CD",
  A100: "#5D76C8",
  A200: "#3E6AC7",
  A300: "#355DB0",
  A400: "#2D5199",
  A500: "#243D73",
  A600: "#1A2A4D",
};

export const IgGreenPrimitives = {
  50: "#F8FFFB",
  100: "#E3FFED",
  200: "#CDFEE1",
  300: "#B4FED2",
  400: "#92FEC2",
  500: "#63FDB0",
  600: "#38FAA3",
  700: "#35EE9B",
  800: "#32E193",
  900: "#2ED389",
  A100: "#32A06E",
  A200: "#29845A",
  A300: "#136F45",
  A400: "#0C5132",
  A500: "#083D25",
  A600: "#092A1B",
};

export const IgOrangePrimitives = {
  50: "#FFFDFA",
  100: "#FFF7EE",
  200: "#FFF1E3",
  300: "#FFEBD5",
  400: "#FFE4C6",
  500: "#FFDDB6",
  600: "#FFD6A4",
  700: "#FFC879",
  800: "#FFB800",
  900: "#E5A500",
  A100: "#B28400",
  A200: "#956F00",
  A300: "#7C5800",
  A400: "#5E4200",
  A500: "#412D00",
  A600: "#251A00",
};

export const IgRedPrimitives = {
  50: "#FFFBFB",
  100: "#FFF6F6",
  200: "#FFEDEC",
  300: "#FEE9E8",
  400: "#FEE2E1",
  500: "#FEDAD9",
  600: "#FED3D1",
  700: "#FEC3C1",
  800: "#FDB0AC",
  900: "#FD817A",
  A100: "#EF4D2F",
  A200: "#E51C00",
  A300: "#B5260B",
  A400: "#8E1F0B",
  A500: "#5F1507",
  A600: "#2F0A04",
};

export const IgAzurePrimitives = {
  50: "#FBFDFF",
  100: "#F2F9FF",
  200: "#EAF4FF",
  300: "#E0F0FF",
  400: "#D5EBFF",
  500: "#CAE6FF",
  600: "#C0E1FF",
  700: "#A8D8FF",
  800: "#91D0FF",
  900: "#51C0FF",
  A100: "#0094D5",
  A200: "#007CB4",
  A300: "#00679B",
  A400: "#00527C",
  A500: "#003A5A",
  A600: "#002133",
};

export const VariationColors = [
  IgBluePrimitives["A200"],
  IgGreenPrimitives["A100"],
  IgBasePrimitives[500],
  "#9C4DDD", // TODO - CP
  IgOrangePrimitives[800],
  IgAzurePrimitives[800],
];

export const IgTonalTextColors = {
  info: IgAzurePrimitives["A200"],
  success: IgGreenPrimitives["A300"],
  warning: IgOrangePrimitives["A300"],
  error: IgRedPrimitives["A200"],
};

export const IgInteractiveTextColors = {
  accent: {
    default: IgBluePrimitives["A200"],
    hover: IgBluePrimitives["A300"],
    active: IgBluePrimitives["A400"],
    disabled: IgBluePrimitives[600],
    focused: IgBluePrimitives["A200"],
  },
  dark: {
    default: IgGrayPrimitives[100],
    hover: IgGrayPrimitives[500],
    active: IgGrayPrimitives[50],
    disabled: IgGrayPrimitives["A100"],
    focused: IgGrayPrimitives[100],
  },
  neutral: {
    default: IgGrayPrimitives["A500"],
    hover: IgGrayPrimitives["A300"],
    active: IgGrayPrimitives["A600"],
    disabled: IgGrayPrimitives["A200"],
    focused: IgGrayPrimitives["A600"],
  },
};

export const IgCoreTextColors = {
  primary: IgGrayPrimitives["A600"],
  secondary: IgGrayPrimitives["A400"],
  tertiary: IgGrayPrimitives["A300"],
  placeholder: IgGrayPrimitives["A200"],
  dark: IgGrayPrimitives[50],
  accent: IgBluePrimitives["A200"],
  tonal: IgTonalTextColors,
  interactive: IgInteractiveTextColors,
};

export const IgTonalBackgroundColors = {
  info: IgAzurePrimitives[100],
  infoSolid: IgAzurePrimitives["A200"],
  success: IgGreenPrimitives[100],
  successSolid: IgGreenPrimitives["A200"],
  warning: IgOrangePrimitives[200],
  warningSolid: IgOrangePrimitives["A200"],
  error: IgRedPrimitives[100],
  errorSolid: IgRedPrimitives["A100"],
};

export const IgInteractiveBackgroundColors = {
  accent: {
    default: IgBluePrimitives["A200"],
    hover: IgBluePrimitives["A300"],
    active: IgBluePrimitives["A400"],
    disabled: IgBluePrimitives[600],
    focused: IgBluePrimitives["A500"],
  },
  neutral: {
    default: IgGrayPrimitives[300],
    hover: IgGrayPrimitives[200],
    active: IgGrayPrimitives[600],
    disabled: IgGrayPrimitives[100],
    focused: IgGrayPrimitives[600],
  },
  dark: {
    default: IgGrayPrimitives["A500"],
    hover: IgGrayPrimitives["A400"],
    active: IgGrayPrimitives["A600"],
    disabled: IgGrayPrimitives[900],
    focused: IgGrayPrimitives["A600"],
  },
  error: {
    default: IgRedPrimitives["A100"],
    hover: IgRedPrimitives["A200"],
    active: IgRedPrimitives["A300"],
    disabled: IgRedPrimitives[700],
    focused: IgRedPrimitives["A400"],
  },
  warning: {
    default: IgOrangePrimitives[800],
    hover: IgOrangePrimitives[900],
    active: IgOrangePrimitives["A100"],
    disabled: IgOrangePrimitives[600],
    focused: IgOrangePrimitives["A200"],
  },
  success: {
    default: IgGreenPrimitives["A100"],
    hover: IgGreenPrimitives["A200"],
    active: IgGreenPrimitives["A300"],
    disabled: IgGreenPrimitives[400],
    focused: IgGreenPrimitives["A400"],
  },
};

export const IgCoreBackgroundColors: IgCoreBackgroundColorsType = {
  baseLightHigh: IgGrayPrimitives[50],
  baseLightMid: IgGrayPrimitives[100],
  baseLightLow: IgGrayPrimitives[200],
  baseDark: IgGrayPrimitives["A500"],
  accent: IgBluePrimitives[100],
  accentSolid: IgBluePrimitives["A200"],
  tonal: IgTonalBackgroundColors,
  interactive: IgInteractiveBackgroundColors,
};

export const IgBorderColors: IgBorderColorsType = {
  core: {
    default: IgGrayPrimitives[800],
    hover: IgGrayPrimitives["A100"],
    active: IgGrayPrimitives["A200"],
    disabled: IgGrayPrimitives[600],
  },
  accent: {
    default: IgBluePrimitives["A200"],
    hover: IgBluePrimitives["A300"],
    active: IgBluePrimitives["A400"],
    disabled: IgBluePrimitives[500],
  },
  success: {
    default: IgGreenPrimitives[800],
    hover: IgGrayPrimitives["A100"],
    active: IgGrayPrimitives["A200"],
    disabled: IgGrayPrimitives[600],
  },
  error: {
    default: IgRedPrimitives[900],
    hover: IgRedPrimitives["A100"],
    active: IgRedPrimitives["A300"],
    disabled: IgRedPrimitives[700],
  },
  info: {
    default: IgAzurePrimitives[900],
    hover: IgAzurePrimitives["A100"],
    active: IgAzurePrimitives["A300"],
    disabled: IgAzurePrimitives[700],
  },
  warning: {
    default: IgOrangePrimitives[800],
    hover: IgOrangePrimitives[900],
    active: IgOrangePrimitives["A200"],
    disabled: IgOrangePrimitives[600],
  },
};

export const IgIconColors: IgIconColorType = {
  light: IgGrayPrimitives["A400"],
  lightHover: IgGrayPrimitives["A600"],
  dark: IgGrayPrimitives[200],
  darkHover: IgGrayPrimitives[500],
  accent: IgBluePrimitives["A100"],
  accentHover: IgBluePrimitives["A300"],
  info: IgAzurePrimitives["A100"],
  infoHover: IgAzurePrimitives["A300"],
  success: IgGreenPrimitives["A100"],
  successHover: IgGreenPrimitives["A300"],
  warning: IgOrangePrimitives[800],
  warningHover: IgOrangePrimitives["A100"],
  error: IgRedPrimitives["A200"],
  errorHover: IgRedPrimitives["A300"],
};

/**
 * @see Theme
 * @see IgCustomTheme
 */
export type IgCustomTheme = Modify<
  Theme,
  {
    palette: {
      common: CommonColors;
      mode: PaletteMode;
      contrastThreshold: number;
      tonalOffset: PaletteTonalOffset;
      primary: PaletteColor;
      secondary: PaletteColor;
      error: PaletteColor;
      warning: PaletteColor;
      info: PaletteColor;
      success: PaletteColor;
      grey: Color;
      text: TypeText & CustomTypeText;
      divider: TypeDivider;
      action: TypeAction;
      background: TypeBackground;
      getContrastText: (background: string) => string;
      augmentColor: (options: PaletteAugmentColorOptions) => PaletteColor;
      accent: Palette["primary"];
      link: Palette["primary"];
    };
    typography: {
      body3: CSSProperties;
      display: CSSProperties;
      code: CSSProperties;
    } & Theme["typography"];
    igShadows: CustomShadows;
    igBorders: IgBorderColorsType;
    igIconColors: IgIconColorType;
    igBackgroundColors: IgCoreBackgroundColorsType;
    igPrimitives: IgPrimitivesType;
    paper: object;
    pageContent: object;
    page: object;
  }
>;

export function createCustomTheme(mode?: PaletteMode): IgCustomTheme {
  const baseTheme = createTheme({
    palette: {
      // NOT TO BE USED
      primary: {
        main: IgCoreTextColors.primary,
        light: IgCoreTextColors.primary,
        dark: IgCoreTextColors.primary,
      },
      // NOT TO BE USED
      secondary: {
        main: IgCoreTextColors.secondary,
        light: IgCoreTextColors.secondary,
        dark: IgCoreTextColors.secondary,
      },
      // NOT TO BE USED
      error: {
        main: IgCoreTextColors.tonal.error,
        light: IgCoreTextColors.tonal.error,
        dark: IgCoreTextColors.tonal.error,
      },
      // NOT TO BE USED
      warning: {
        main: IgCoreTextColors.tonal.warning,
        light: IgCoreTextColors.tonal.warning,
        dark: IgCoreTextColors.tonal.warning,
      },
      // NOT TO BE USED
      info: {
        main: IgCoreTextColors.tonal.info,
        light: IgCoreTextColors.tonal.info,
        dark: IgCoreTextColors.tonal.info,
      },
      // NOT TO BE USED
      success: {
        main: IgCoreTextColors.tonal.success,
        light: IgCoreTextColors.tonal.success,
        dark: IgCoreTextColors.tonal.success,
      },
      grey: IgGrayPrimitives,
      common: {
        black: "#1E1E1E",
      },
    },
    spacing: 4,
    typography: {
      body1: {
        fontSize: "14px",
        lineHeight: "120%",
        fontFamily: IgFontFamily.body,
      },
      body2: {
        lineHeight: "16px",
        fontSize: "12px",
        fontFamily: IgFontFamily.body,
      },
      subtitle2: {
        lineHeight: "18px",
        letterSpacing: "0.25px",
        fontFamily: IgFontFamily.body,
      },
      h1: {
        lineHeight: "30px",
        fontSize: "22px",
        fontFamily: IgFontFamily.heading,
      },
      h2: {
        lineHeight: "26px",
        fontSize: "18px",
        fontFamily: IgFontFamily.heading,
      },
      h3: {
        lineHeight: "24px",
        fontSize: "16px",
        fontFamily: IgFontFamily.heading,
      },
      allVariants: {
        color: IgCoreTextColors.primary,
        fontWeight: 400,
      },
      fontFamily: IgFontFamily.body,
      fontWeightMedium: 600,
      fontWeightBold: 700,
    },
    components: {
      MuiList: {
        styleOverrides: {
          root: {
            paddingTop: "0px",
            paddingBottom: "0px",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "15px",
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          size: "small",
          color: "secondary",
        },
        styleOverrides: {
          root: {
            backgroundColor: IgCoreBackgroundColors.baseLightHigh,
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "6px",
              border: `1px solid ${IgBorderColors.core.default}`,
            },
            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: IgBorderColors.accent.active,
            },
            "& .MuiInputBase-root.Mui-disabled": {
              "& > fieldset": {
                borderColor: IgBorderColors.core.disabled,
              },
            },
            "& .MuiSvgIcon-root": {
              color: IgIconColors.light,
              "&.Mui-disabled": {
                color: IgCoreTextColors.placeholder,
              },
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            boxShadow: `${IgShadows.low} !important`,
            borderRadius: "6px",
            border: `1px solid ${IgBorderColors.core.default}`,
            padding: "4px",
            backgroundColor: IgCoreBackgroundColors.baseLightLow,
          },
          popper: {
            borderRadius: "6px",
            textDecoration: "none",
            textTransform: "none",
            backgroundColor: IgCoreBackgroundColors.interactive.neutral.default,
          },
        },
      },
      MuiAccordion: {
        defaultProps: {
          elevation: 0,
          disableGutters: true,
          TransitionProps: {
            unmountOnExit: true,
          },
        },
        styleOverrides: {
          root: {
            backgroundColor: IgCoreBackgroundColors.baseLightHigh,
            border: `1px solid ${IgBorderColors.core.default}`,
            borderRadius: "8px !important",
            padding: "12px 16px 16px 16px",
            "&:hover": {
              borderShadow: IgShadows.low,
              border: `1px solid ${IgBorderColors.core.hover}`,
            },
            "&.MuiAccordionSummary-content": {
              margin: "0px 0px",
              marginBottom: "8px",
            },
          },
        },
      },
      MuiAvatar: {
        variants: [
          {
            props: { variant: "rounded" },
            style: { borderRadius: "8px", width: "32px", height: "32px" },
          },
        ],
      },
      MuiFormControl: {
        defaultProps: {
          size: "small",
        },
      },
      MuiInput: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          input: {
            "&::placeholder": {
              fontSize: "14px",
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            boxShadow: `${IgShadows.low} !important`,
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { color: "accent" },
                style: {
                  backgroundColor:
                    IgCoreBackgroundColors.interactive.accent.default,
                },
              },
              {
                props: { color: "accentDisabled" },
                style: {
                  backgroundColor:
                    IgCoreBackgroundColors.interactive.accent.disabled,
                },
              },
              {
                props: { color: "default" },
                style: {
                  backgroundColor: IgBorderColors.core.default,
                },
              },
            ],
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { color: "accent" },
                style: { color: IgIconColors.accent },
              },
            ],
          },
          colorInfo: {
            backgroundColor: "#EEEEEE !important",
            color: "rgba(0,0,0,.38)",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: `${IgShadows.low} !important`,
            borderRadius: "6px",
            border: `1px solid ${IgBorderColors.core.default}`,
            padding: "4px",
            backgroundColor: IgCoreBackgroundColors.baseLightLow,
          },
          list: {
            borderRadius: "6px",
            textDecoration: "none",
            textTransform: "none",
            backgroundColor: IgCoreBackgroundColors.interactive.neutral.default,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&:hover": { backgroundColor: "#F1F1F1" },
            "&.Mui-selected": {
              backgroundColor: "#F1F1F1 !important",
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          root: {
            marginLeft: "190px",
            // "& .MuiPaper-root": {
            //   boxShadow: IgShadows.low,
            // },
          },
        },
      },
      MuiChip: {
        defaultProps: { variant: "outlined", size: "small" },
        styleOverrides: {
          root: {
            variants: [
              {
                props: {
                  color: "accent",
                },
                style: {
                  border: `1px solid ${IgBorderColors.accent.default}`,
                  backgroundColor: IgCoreBackgroundColors.accent,
                },
              },
            ],
            fontSize: "14px",
            lineHeight: "16px",
          },
        },
        variants: [
          {
            props: {
              color: "info",
            },
            style: {
              border: `1px solid ${IgBorderColors.info.default}`,
              backgroundColor: IgCoreBackgroundColors.tonal.info,
            },
          },

          {
            props: {
              color: "warning",
            },
            style: {
              border: `1px solid ${IgBorderColors.warning.default}`,
              backgroundColor: IgCoreBackgroundColors.tonal.warning,
            },
          },
          {
            props: {
              color: "error",
            },
            style: {
              border: `1px solid ${IgBorderColors.error.default}`,
              backgroundColor: IgCoreBackgroundColors.tonal.error,
            },
          },
          {
            props: {
              color: "success",
            },
            style: {
              border: `1px solid ${IgBorderColors.success.default}`,
              backgroundColor: IgCoreBackgroundColors.tonal.success,
            },
          },
          {
            props: {
              color: "default",
            },
            style: {
              border: `1px solid ${IgBorderColors.core.default}`,
              backgroundColor:
                IgCoreBackgroundColors.interactive.neutral.default,
            },
          },
          {
            props: {
              size: "small",
            },
            style: {
              fontSize: "12px",
              color: IgCoreTextColors.primary,
              fontWeight: 400,
              height: "24px !important",
              borderRadius: "16px",
            },
          },
          {
            props: {
              size: "medium",
            },
            style: {
              color: IgCoreTextColors.primary,
              fontWeight: 600,
              height: "32px !important",
              borderRadius: "16px",
            },
          },
        ],
      },
      MuiIconButton: {
        defaultProps: {
          color: "default",
        },
        styleOverrides: {
          root: {
            borderRadius: "6px",
            padding: "8px",
            "& .MuiTouchRipple-root .MuiTouchRipple-child": {
              borderRadius: "6px",
            },
          },
        },
        variants: [
          {
            props: {
              color: "default",
            },
            style: {
              color: IgIconColors.light,
            },
          },
        ],
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "none",

            "& .Mui-selected": {
              backgroundColor: IgInteractiveBackgroundColors.neutral.active,
            },
            "&.MuiToggleButton-root:not(.Mui-selected)": {
              color: IgCoreTextColors.tertiary,
            },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: 99999,
            height: "28px",
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: mode === "dark" ? IgIconColors.dark : IgIconColors.light,
            variants: [
              {
                props: { color: "accent" },
                style: {
                  color: IgIconColors.accent,
                },
              },
              {
                props: { color: "dark" },
                style: { color: IgIconColors.dark },
              },
              {
                props: { color: "light" },
                style: { color: IgIconColors.light },
              },
            ],
          },
        },
        variants: [
          {
            props: { color: "success" },
            style: { color: IgIconColors.success },
          },
          {
            props: { color: "primary" },
            style: { color: IgIconColors.light },
          },
          {
            props: { color: "info" },
            style: { color: IgIconColors.info },
          },
          {
            props: { color: "warning" },
            style: { color: IgIconColors.warning },
          },
          {
            props: { color: "error" },
            style: { color: IgIconColors.error },
          },
        ],
      },
      MuiCheckbox: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          root: {
            // "& .MuiSvgIcon-root": {
            //   color: IgBorderColors.core.default,
            // },
            ...(mode === "dark" && {
              "& .MuiSvgIcon-root": {
                color: IgIconColors.light,
              },
            }),
            "&.MuiButtonBase-root.MuiCheckbox-root": {
              borderRadius: "6px !important",
              height: "32px !important",
              width: "32px !important",
            },
            "& .MuiTouchRipple-root .MuiTouchRipple-child": {
              borderRadius: "6px",
            },
            "&.Mui-disabled": {
              "& .MuiSvgIcon-root": {
                color: IgBorderColors.core.default,
              },
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            "& .MuiSvgIcon-root": { color: IgIconColors.light },
            padding: "8px !important",
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: "8px",
            borderRadius: "9999px",
            backgroundColor: IgBorderColors.core.default,
          },
          bar: {
            backgroundColor: IgCoreBackgroundColors.accentSolid,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            backgroundColor: IgCoreBackgroundColors.baseLightHigh,

            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "6px",
              border: `1px solid ${IgBorderColors.core.default}`,
            },

            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": {
                borderColor: IgBorderColors.accent.active,
              },
            },

            "& .MuiInputBase-root.Mui-disabled": {
              "& > fieldset": {
                borderColor: IgBorderColors.core.disabled,
              },
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { color: "tertiary" },
                style: { color: IgCoreTextColors.tertiary },
              },
              {
                props: { color: "link" },
                style: { color: IgCoreTextColors.accent },
              },
              {
                props: { color: "placeholder" },
                style: { color: IgCoreTextColors.placeholder },
              },
              {
                props: { color: "dark" },
                style: { color: IgCoreTextColors.dark },
              },
              {
                props: { color: "igSecondary" },
                style: {
                  color: IgCoreTextColors.secondary,
                },
              },
            ],
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              fontWeight: 600,
            },
            textTransform: "none",
            "&.Mui-disabled": {
              color: IgCoreTextColors.interactive.neutral.disabled,
              fontWeight: 400,
            },
            "&:hover": {
              backgroundColor: IgCoreBackgroundColors.interactive.neutral.hover,
              borderTopRightRadius: "6px",
              borderTopLeftRadius: "6px",
            },
          },
          textColorPrimary: {
            color: IgCoreTextColors.interactive.neutral.default,
            fontWeight: 400,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: IgBorderColors.accent.active,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            minHeight: "36px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            lineHeight: "16.8px",
            "& .MuiButton-icon": { color: IgIconColors.light },
            variants: [
              {
                props: { color: "link" },
                style: {
                  color: IgCoreTextColors.interactive.accent.default,
                  backgroundColor: "transparent",
                  ":hover": {
                    color: IgCoreTextColors.interactive.accent.hover,
                    backgroundColor: "transparent",
                  },
                  ":disabled": {
                    color: IgCoreTextColors.interactive.accent.disabled,
                    backgroundColor: "transparent",
                  },
                  ":focused": {
                    color: IgCoreTextColors.interactive.accent.focused,
                    backgroundColor: "transparent",
                  },
                },
              },
              {
                props: { size: "extraSmall" },
                style: {
                  minHeight: "32px !important",
                  padding: "7.5px 12px",
                },
              },
            ],
          },
        },
        defaultProps: {
          color: "primary",
          variant: "contained",
          disableElevation: true,
          size: "small",
        },
        variants: [
          {
            props: { variant: "contained" },
            style: {
              paddingLeft: "12px",
              paddingRight: "12px",
            },
          },
          {
            props: { color: "primary" },
            style: {
              color: IgCoreTextColors.dark,
              backgroundColor:
                IgCoreBackgroundColors.interactive.accent.default,
              ":hover": {
                backgroundColor:
                  IgCoreBackgroundColors.interactive.accent.hover,
              },
              ":disabled": {
                backgroundColor:
                  IgCoreBackgroundColors.interactive.accent.disabled,
              },
              ":focused": {
                backgroundColor:
                  IgCoreBackgroundColors.interactive.accent.focused,
              },
            },
          },
          {
            props: { color: "secondary" },
            style: {
              color: IgCoreTextColors.primary,
              backgroundColor:
                IgCoreBackgroundColors.interactive.neutral.default,
              ":hover": {
                backgroundColor:
                  IgCoreBackgroundColors.interactive.neutral.hover,
              },
              ":disabled": {
                backgroundColor:
                  IgCoreBackgroundColors.interactive.neutral.disabled,
              },
              ":focused": {
                backgroundColor:
                  IgCoreBackgroundColors.interactive.neutral.focused,
                color: IgCoreTextColors.secondary,
              },
            },
          },
          {
            props: { color: "error" },
            style: {
              color: IgCoreTextColors.dark,
              backgroundColor: IgCoreBackgroundColors.interactive.error.default,
              ":hover": {
                backgroundColor: IgCoreBackgroundColors.interactive.error.hover,
              },
              ":disabled": {
                backgroundColor:
                  IgCoreBackgroundColors.interactive.error.disabled,
              },
              ":focused": {
                backgroundColor:
                  IgCoreBackgroundColors.interactive.error.focused,
              },
            },
          },
          {
            props: { variant: "outlined" },
            style: {
              color: IgCoreTextColors.primary,
              borderColor: IgBorderColors.core.default,
              backgroundColor: "transparent",
              borderRadius: "6px",
              ":hover": {
                backgroundColor:
                  IgCoreBackgroundColors.interactive.neutral.hover,
                borderColor: IgBorderColors.core.hover,
              },
              ":disabled": {
                backgroundColor: "transparent",
              },
              ":focused": {
                backgroundColor: "transparent",
              },
            },
          },
          {
            props: { variant: "text" },
            style: {
              color: IgCoreTextColors.primary,
              backgroundColor: "transparent",
              ":hover": {
                backgroundColor:
                  IgCoreBackgroundColors.interactive.neutral.hover,
              },
              ":disabled": {
                backgroundColor: "transparent",
              },
              ":focused": {
                backgroundColor: "transparent",
              },
            },
          },
        ],
      },
      MuiSwitch: {
        defaultProps: {
          color: "primary",
        },
        styleOverrides: {
          root: {
            width: 36,
            height: 19,
            padding: 0,
            "& .MuiSwitch-switchBase": {
              padding: 0,
              margin: 2,
              transitionDuration: "300ms",
              "&.Mui-checked": {
                transform: "translateX(16px)",
                "& + .MuiSwitch-track": {
                  opacity: 1,
                  border: 0,
                },
                "&.Mui-disabled + .MuiSwitch-track": {
                  opacity: 0.5,
                },
              },
              "&.Mui-disabled + .MuiSwitch-track": {
                opacity: 0.7,
              },
            },
            "& .MuiSwitch-thumb": {
              boxSizing: "border-box",
              width: 15,
              height: 15,
            },
            "& .MuiSwitch-track": {
              borderRadius: 26 / 2,
              opacity: 1,
            },
            variants: [
              {
                props: { color: "plugin" },
                style: {
                  "& .MuiSwitch-switchBase": {
                    "&.Mui-checked": {
                      color: IgBorderColors.warning.default,

                      "& + .MuiSwitch-track": {
                        backgroundColor:
                          IgInteractiveBackgroundColors.accent.default,
                        boxShadow: `inset 0 0 0 2px ${IgBorderColors.warning.default}`,
                      },
                    },
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: "transparent",
                    boxShadow: `inset 0 0 0 2px white`,
                  },
                },
              },
            ],
          },
        },
        variants: [
          {
            props: { color: "primary" },
            style: {
              ":hover": {
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: IgInteractiveBackgroundColors.accent.hover,
                },
                "& .MuiSwitch-track": {
                  backgroundColor: IgGrayPrimitives["A200"],
                },
              },
              ":disabled": {
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor:
                    IgInteractiveBackgroundColors.accent.disabled,
                },
                "& .MuiSwitch-track": {
                  backgroundColor: IgInteractiveBackgroundColors.dark.disabled,
                },
              },
              ":active": {
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: IgInteractiveBackgroundColors.accent.active,
                  boxShadow: `inset 0 0 0 2px ${IgInteractiveBackgroundColors.dark.active}`,
                },
                "& .MuiSwitch-track": {
                  boxShadow: `inset 0 0 0 2px ${IgInteractiveBackgroundColors.dark.active}`,
                  backgroundColor: IgGrayPrimitives["A200"],
                },
              },
              ":focused": {
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: IgInteractiveBackgroundColors.accent.focused,
                  boxShadow: `inset 0 0 0 2px ${IgBorderColors.warning.default}`,
                },
                "& .MuiSwitch-track": {
                  boxShadow: `inset 0 0 0 2px ${IgBorderColors.accent.active}`,
                  backgroundColor: IgGrayPrimitives["A200"],
                },
              },
              "& .MuiSwitch-switchBase": {
                "&.Mui-checked": {
                  color: IgCoreBackgroundColors.baseLightHigh,
                  "& + .MuiSwitch-track": {
                    backgroundColor:
                      IgInteractiveBackgroundColors.accent.default,
                  },
                },
              },
              "& .MuiSwitch-track": {
                backgroundColor: IgGrayPrimitives["A100"],
              },
            },
          },
          {
            props: { color: "secondary" },
            style: {
              ":hover": {
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: IgInteractiveBackgroundColors.dark.disabled,
                },
                "& .MuiSwitch-track": {
                  backgroundColor: IgInteractiveBackgroundColors.neutral.hover,
                },
              },
              ":disabled": {
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: IgInteractiveBackgroundColors.dark.disabled,
                },
                "& .MuiSwitch-track": {
                  backgroundColor: IgInteractiveBackgroundColors.dark.disabled,
                },
              },
              ":active": {
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: IgInteractiveBackgroundColors.dark.active,
                  boxShadow: `inset 0 0 0 2px ${IgInteractiveBackgroundColors.dark.default}`,
                },
                "& .MuiSwitch-track": {
                  boxShadow: `inset 0 0 0 2px ${IgInteractiveBackgroundColors.dark.default}`,
                  backgroundColor: IgInteractiveBackgroundColors.dark.active,
                },
              },
              ":focused": {
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: IgInteractiveBackgroundColors.dark.focused,
                  boxShadow: `inset 0 0 0 2px ${IgBorderColors.core.default}`,
                },
                "& .MuiSwitch-track": {
                  boxShadow: `inset 0 0 0 2px ${IgBorderColors.core.default}`,
                  backgroundColor: IgInteractiveBackgroundColors.dark.focused,
                },
              },
              "& .MuiSwitch-switchBase": {
                "&.Mui-checked": {
                  color: IgInteractiveBackgroundColors.dark.default,
                  "& + .MuiSwitch-track": {
                    backgroundColor:
                      IgInteractiveBackgroundColors.neutral.default,
                  },
                },
                color: IgInteractiveBackgroundColors.dark.default,
              },
              "& .MuiSwitch-track": {
                backgroundColor: IgInteractiveBackgroundColors.dark.disabled,
              },
            },
          },
        ],
      },
    },
  });
  return {
    ...baseTheme,
    typography: {
      ...baseTheme.typography,
      body3: {
        fontSize: "10px",
        lineHeight: "14px",
      },
      display: {
        fontSize: "32px",
        lineHeight: "40px",
      },
      code: {
        fontSize: "14px",
        lineHeight: "20px",
        fontFamily: IgFontFamily.code,
      },
    },
    palette: {
      ...baseTheme.palette,
      text: {
        ...baseTheme.palette.text,
        tertiary: IgCoreTextColors.tertiary,
        placeholder: IgCoreTextColors.placeholder,
        dark: IgCoreTextColors.dark,
        accent: IgCoreTextColors.accent,
        tonal: IgCoreTextColors.tonal,
        interactive: IgCoreTextColors.interactive,
      },
      accent: {
        main: IgCoreBackgroundColors.interactive.accent.default,
        dark: IgCoreBackgroundColors.interactive.accent.focused,
        light: IgCoreBackgroundColors.interactive.accent.disabled,
        contrastText: IgCoreTextColors.dark,
      },
      link: {
        main: IgCoreBackgroundColors.interactive.accent.default,
        dark: IgCoreBackgroundColors.interactive.accent.focused,
        light: IgCoreBackgroundColors.interactive.accent.disabled,
        contrastText: IgCoreTextColors.dark,
      },
    },
    paper: {
      borderRadius: "8px",
    },
    pageContent: {
      maxWidth: "1450px",
    },
    page: {
      maxWidth: "1600px",
    },
    igShadows: IgShadows,
    igBorders: IgBorderColors,
    igIconColors: IgIconColors,
    igBackgroundColors: IgCoreBackgroundColors,
    igPrimitives: {
      gray: IgGrayPrimitives,
      green: IgGreenPrimitives,
      orange: IgOrangePrimitives,
      red: IgRedPrimitives,
      azure: IgAzurePrimitives,
      base: IgBasePrimitives,
      blue: IgBluePrimitives,
    },
  };
}

interface LibraryThemeProviderProps {
  children?: ReactNode;
  theme: IgCustomTheme;
}

export const IgTheme = createCustomTheme();

export const IntelligemsThemeProvider: FC<LibraryThemeProviderProps> = ({
  children,
  theme,
}) => <ThemeProvider theme={theme as Theme}>{children}</ThemeProvider>;

type SxProp = SxProps<typeof IgTheme>;
export type Styles = Record<string, SxProp>;
